/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { HiddenProps } from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isItCheckoutPage } from "@monsma/App/Utilities/helpers";
import { screen } from "@monsma/XDL/Helpers";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// xng-change
const mapStateToProps = (state: ApplicationState) => {
    return {
        pageType: getCurrentPage(state).type,
    };
};

type Props = PageProps & ReturnType<typeof mapStateToProps>; // xng-change

export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    navigationWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden: HiddenProps;
    mobileSpacer: InjectableCss;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;

            &.checkout-header {
                align-items: center;
                height: 100%;
            }
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            margin-top: 13px;

            a > span , img {
                max-height: 44px;
            }

            ${screen("lte", "md")} {
                display: flex;
                margin-top: 0;
                padding: 0 5px;
                order: 3;
                flex: 1;

                & > div {
                    display: flex;
                    justify-content: center;

                    & > div {
                        display: flex;
                        justify-content: center;
                    }
                }
            }

            &.checkout-header {
                margin-top: 0;

                ${screen("lte", "md")} {
                    order: unset;
                    flex: 0 0 auto;
                    padding: 0;
                }

                ${screen("lte", "sm")} {
                    padding-left: 17px;
                }
            }
        `,
    },
    navigationWrapper: {
        css: css`
            flex: 1;

            ${screen("lte", "md")} {
                order: 1;
                flex: 0;
                max-width: 64px;
            }

            &.hide {
                display: none;
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;

            ${screen("lte", "md")} {
                order: 2;
                max-width: 64px;

                &.checkout-header {
                    order: unset;
                    max-width: unset;
                    flex: 0 0 auto;
                }
            }

            ${screen("lte", "sm")} {
                padding-right: 17px;
            }
        `,
    },
    linkListHidden: {
        below: "lg",
    },
    mobileSpacer: {
        css: css`
            order: 4;

            ${screen("lte", "md")} {
                width: 119px;
            }

            &.hide {
                display: none;
            }
        `,
    },
};

const LogoLinksSearch: React.FC<Props> = ({ id, pageType }) => {
    // xng-change: update the template
    return (
        <StyledWrapper
            {...logoLinksSearchStyles.container}
            className={`${isItCheckoutPage(pageType) && "checkout-header"}`}
        >
            <StyledWrapper
                {...logoLinksSearchStyles.logoWrapper}
                className={`${isItCheckoutPage(pageType) && "checkout-header"}`}
            >
                <Zone contentId={id} zoneName="Logo" />
            </StyledWrapper>
            <StyledWrapper
                {...logoLinksSearchStyles.navigationWrapper}
                className={`${isItCheckoutPage(pageType) && "hide"}`}
            >
                <Zone contentId={id} zoneName="Navigation" />
            </StyledWrapper>
            <StyledWrapper
                {...logoLinksSearchStyles.searchMenuWrapper}
                className={`${isItCheckoutPage(pageType) && "checkout-header"}`}
            >
                <Zone contentId={id} zoneName="Search" />
            </StyledWrapper>
            <StyledWrapper
                {...logoLinksSearchStyles.mobileSpacer}
                className={`${isItCheckoutPage(pageType) && "hide"}`}
            />
        </StyledWrapper>
    );
};

const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps)(LogoLinksSearch), // xng-change
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
